import React, { useState, useRef, useEffect } from 'react';
import { Calendar as CalendarIcon, ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react';
import {
    format,
    addMonths,
    subMonths,
    startOfMonth,
    endOfMonth,
    startOfWeek,
    endOfWeek,
    isSameMonth,
    isSameDay,
    addDays,
    eachDayOfInterval,
    isValid
} from 'date-fns';
import { it } from 'date-fns/locale';
import { motion, AnimatePresence } from 'framer-motion';
import { useEvents } from '@/contexts/EventsContext';
import { useAuth } from '@/contexts/AuthContext';

interface DatePickerProps {
    selected?: Date;
    onSelect: (date: Date | undefined) => void;
    placeholder?: string;
    className?: string;
    disabled?: boolean;
    minDate?: Date;
    hairdresserId?: string;
    allowClosedDays?: boolean;
}

const DatePicker: React.FC<DatePickerProps> = ({
    selected,
    onSelect,
    placeholder,
    className = "",
    disabled = false,
    minDate,
    hairdresserId,
    allowClosedDays = false
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [currentMonth, setCurrentMonth] = useState(new Date());
    const containerRef = useRef<HTMLDivElement>(null);
    const { busySlots } = useEvents();
    const { organization } = useAuth();

    // Check if the shop is closed on a specific date
    const isClosed = (date: Date) => {
        if (!organization?.time_ranges) return false;

        const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
        const dayName = days[date.getDay()];
        const schedule = organization.time_ranges[dayName];

        // Se open è nullo, il salone è chiuso
        return !schedule || !schedule.open;
    };

    // Formattazione data visualizzata nel campo
    const displayValue = selected && isValid(selected)
        ? format(selected, 'dd MMMM yyyy', { locale: it })
        : null;

    // Chiudi al click esterno
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Check if a date is a holiday
    const isHoliday = (date: Date) => {
        if (!busySlots) return false;

        return busySlots.some((slot: any) => {
            if (slot.type !== 'holiday') return false;

            // Shop holiday (hairdresser_id is null) or specific staff holiday
            if (slot.hairdresser_id && slot.hairdresser_id !== hairdresserId) return false;

            const start = new Date(slot.start_date);
            const end = new Date(slot.end_date);

            // Set times to midnight for accurate date comparison
            const dateCheck = new Date(date).setHours(0, 0, 0, 0);
            const startCheck = new Date(start).setHours(0, 0, 0, 0);
            const endCheck = new Date(end).setHours(0, 0, 0, 0);

            return dateCheck >= startCheck && dateCheck <= endCheck;
        });
    };

    // LOGICA CALENDARIO
    const renderHeader = () => {
        return (
            <div className="flex items-center justify-between mb-4 px-1">
                <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); setCurrentMonth(subMonths(currentMonth, 1)); }}
                    className="p-1 hover:bg-gray-100 rounded-lg text-gray-500 transition-colors"
                >
                    <ChevronLeft size={18} />
                </button>
                <div className="font-bold text-gray-900 capitalize">
                    {format(currentMonth, 'MMMM yyyy', { locale: it })}
                </div>
                <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); setCurrentMonth(addMonths(currentMonth, 1)); }}
                    className="p-1 hover:bg-gray-100 rounded-lg text-gray-500 transition-colors"
                >
                    <ChevronRight size={18} />
                </button>
            </div>
        );
    };

    const renderDays = () => {
        const dateFormat = "EEEE";
        const days = [];
        let startDate = startOfWeek(currentMonth, { locale: it });

        for (let i = 0; i < 7; i++) {
            days.push(
                <div key={i} className="text-center text-xs font-bold text-gray-400 uppercase py-1">
                    {format(addDays(startDate, i), 'EEEEE', { locale: it })}
                </div>
            );
        }
        return <div className="grid grid-cols-7 mb-2">{days}</div>;
    };

    const renderCells = () => {
        const monthStart = startOfMonth(currentMonth);
        const monthEnd = endOfMonth(monthStart);
        const startDate = startOfWeek(monthStart, { locale: it });
        const endDate = endOfWeek(monthEnd, { locale: it });

        const days = eachDayOfInterval({ start: startDate, end: endDate });

        return (
            <div className="grid grid-cols-7 gap-1">
                {days.map((day, i) => {
                    const isSelected = selected ? isSameDay(day, selected) : false;
                    const isCurrentMonth = isSameMonth(day, monthStart);

                    const isDisabled =
                        (minDate ? day < new Date(minDate.setHours(0, 0, 0, 0)) : false) ||
                        isHoliday(day) ||
                        (!allowClosedDays && isClosed(day));

                    return (
                        <div key={i} className="aspect-square">
                            <button
                                type="button"
                                disabled={isDisabled}
                                onClick={() => {
                                    if (!isDisabled) {
                                        onSelect(day);
                                        setIsOpen(false);
                                    }
                                }}
                                className={`
                                    w-full h-full flex items-center justify-center text-sm rounded-lg transition-all
                                    ${!isCurrentMonth ? 'text-gray-300' : 'text-gray-700'}
                                    ${isSelected
                                        ? 'bg-gray-900 text-white font-bold shadow-md'
                                        : isDisabled
                                            ? 'cursor-not-allowed !text-gray-300'
                                            : 'hover:bg-gray-100 hover:text-gray-900'
                                    }
                                    ${isSameDay(day, new Date()) && !isSelected ? 'border border-gray-200 font-bold !text-clip-600' : ''}
                                `}
                            >
                                {format(day, 'd')}
                            </button>
                        </div>
                    );
                })}
            </div>
        );
    };

    return (
        <div className={`relative ${className}`} ref={containerRef}>
            {/* Input Trigger */}
            <div
                onClick={() => !disabled && setIsOpen(!isOpen)}
                className={`w-full h-[48px] flex items-center justify-between px-3 py-2.5 rounded-xl border transition-all cursor-pointer outline-none ${disabled
                    ? 'bg-gray-100 text-gray-400 border-transparent cursor-not-allowed'
                    : isOpen
                        ? 'bg-white border-gray-900 ring-1 ring-gray-900 text-gray-900'
                        : 'bg-gray-50 border-gray-200 hover:bg-white hover:border-gray-300 text-gray-900'
                    }`}
            >
                <div className="flex items-center gap-2 overflow-hidden">
                    <CalendarIcon size={16} className={`flex-shrink-0 ${disabled ? "text-gray-400" : "text-gray-500"}`} />
                    <span className={`font-medium truncate ${!displayValue ? 'text-gray-400' : ''}`}>
                        {displayValue || placeholder || "Seleziona data"}
                    </span>
                </div>
                <ChevronDown size={16} className={`text-gray-400 flex-shrink-0 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
            </div>

            {/* Custom Popover Calendar */}
            <AnimatePresence>
                {isOpen && (
                    <motion.div
                        initial={{ opacity: 0, y: 5, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 5, scale: 0.95 }}
                        transition={{ duration: 0.15 }}
                        className="absolute top-full left-0 mt-2 z-[60] bg-white border border-gray-100 rounded-2xl shadow-xl p-4 w-[300px]"
                    >
                        {renderHeader()}
                        {renderDays()}
                        {renderCells()}
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default DatePicker;